import Swal from "sweetalert2";
import logo from "../assets/logo.jpeg";
import useMenuStore from "../lib/menuStore";
import { database_logs } from "../lib/logActivities";
import { getBase64Logo } from "../utils/getBase64Logo";

const noItemsWarning = () => {
  Swal.fire({ title: "No items to print!", icon: "warning" });
};

// Calculate the dynamic window height based on items and extra details
const calculatedHeight = (orderItems) => {
  const baseHeight = 300; // Height for header, footer, and extra details after table
  const itemHeight = 30; // Estimated height per item row
  const calculatedHeight = baseHeight + orderItems.length * itemHeight;

  return calculatedHeight;
};

// Generate the HTML content for the bill print window
export const printBillOnly = async (
  orderId,
  waiterName,
  chosenTable,
  totalOrdersQty,
  totalOrdersPrice,
  orderItems
) => {
  const { updatePrintedBillStatus } = useMenuStore.getState();
  const app_logo = await getBase64Logo();

  if (!orderItems || orderItems.length === 0) {
    noItemsWarning();
    return;
  }

  // Open a new print window
  const printWindow = window.open(
    "",
    "",
    `width=315,height=${calculatedHeight(orderItems)}`
  );

  // Generate the HTML
  const htmlContent = generatePrintHTML(
    orderId,
    waiterName,
    chosenTable,
    totalOrdersQty,
    totalOrdersPrice,
    orderItems,
    app_logo
  );

  // Write the HTML to the new window
  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  // Wait for all content (especially images) to finish loading
  printWindow.onload = async () => {
    const logoImg = printWindow.document.querySelector("img");

    // Wait for the logo image to load if it exists
    if (logoImg && !logoImg.complete) {
      logoImg.onload = async () => {
        await handlePrintAndClose();
      };
    } else {
      await handlePrintAndClose();
    }
  };

  // Separate helper for printing and cleanup
  async function handlePrintAndClose() {
    printWindow.focus();
    printWindow.print();
    printWindow.close();

    useMenuStore.getState().setBillPrinted(true);

    const details = {
      orderId,
      table: chosenTable,
      totalAmount: totalOrdersPrice,
    };

    database_logs(waiterName, "BILL_PRINTED", details);

    // Update state and DB
    await updatePrintedBillStatus(orderId);
  }
};

// Generate the HTML content for the RECEIPT print window
export const printReceipt = async (
  orderId,
  waiterName,
  chosenTable,
  totalOrdersQty,
  totalOrdersPrice,
  orderItems,
  totalPaid,
  cashValue,
  cardValue,
  change
) => {
  if (!orderItems || orderItems.length === 0) {
    noItemsWarning();
    return;
  }
    
  const app_logo = await getBase64Logo();

  const printWindow = window.open(
    "",
    "",
    `width=315,height=${calculatedHeight(orderItems)}`
  );
  const logo = app_logo;

  const htmlContent = generatePrintReceiptHTML(
    orderId,
    waiterName,
    chosenTable,
    totalOrdersQty,
    totalOrdersPrice,
    orderItems,
    totalPaid,
    cashValue,
    cardValue,
    change,
    logo
  );

  printWindow.document.open();
  printWindow.document.writeln(htmlContent);
  printWindow.document.close();

  const logoElement = printWindow.document.querySelector(".logo");
  if (logoElement) {
    logoElement.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  } else {
    // fallback if logo not found or fails to load
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  }
};

// Generate the HTML content for the KITCHEN print window
export const printForKitchen = async (
  orderId,
  waiterName,
  chosenTable,
  orderItems
) => {
  const { updatePrintedStatus } = useMenuStore.getState();
  const app_logo = await getBase64Logo();

  if (!orderItems || orderItems.length === 0) {
    noItemsWarning();
    return;
  }

  // Open new print window
  const printWindow = window.open(
    "",
    "",
    `width=315,height=${calculatedHeight(orderItems)}`
  );

  // Generate content
  const htmlContent = generateKitchenPrintHTML(
    orderId,
    waiterName,
    chosenTable,
    orderItems,
    app_logo
  );

  // Write to new window
  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  // Wait for everything to load (especially the logo)
  printWindow.onload = async () => {
    const logoImg = printWindow.document.querySelector("img");

    const handlePrint = async () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();

      // Log + update
      const details = {
        orderId,
        table: chosenTable,
      };
      database_logs(waiterName, "PRINTED_FOR_KITCHEN", details);
      await updatePrintedStatus(orderId);
    };

    if (logoImg && !logoImg.complete) {
      logoImg.onload = handlePrint;
      logoImg.onerror = handlePrint;
    } else {
      handlePrint();
    }
  };
};

// Function to generate the HTML content for the print window
const generatePrintHTML = (
  orderId,
  waiterName,
  chosenTable,
  totalOrdersQty,
  totalOrdersPrice,
  orderItems,
  logo
) => {
  const rows = orderItems
    .map((item) => {
      const name =
        item?.menuItems?.type === "food"
          ? `${item.menuItems.item_name.toUpperCase()} ${
              item.menuItems.description
                ? item.menuItems.description.toUpperCase()
                : ""
            }`
          : item?.drink?.type === "drink"
          ? item.drink.item_name.toUpperCase()
          : "UNKNOWN ITEM";

      const price =
        item?.menuItems?.price || item?.drink?.price || item.price || 0;

      return `
          <tr>
            <td>${name}</td>
            <td class="right">${price.toFixed(2)}</td>
            <td class="right">${item.quantity}</td>
            <td class="right">${item.total.toFixed(2)}</td>
          </tr>
        `;
    })
    .join("");

  return `
      <html>
        <head>
          <title>Print Bill</title>
          <style>
            * {
              box-sizing: border-box;
            }
            body {
              font-family: "Arial", sans-serif;
              width: 80mm;
              margin: 0 auto;
              text-align: center;
              padding: 10px;
            }
            .logo {
              display: block;
              margin: 0 auto 10px auto;
              width: 80px;
              height: 80px;
              object-fit: contain;
            }
            h4 {
              margin: 5px 0;
              font-weight: bold;
            }
            h6 {
              margin: 0;
              font-style: italic;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
            }
            th, td {
              border: 1px solid #000;
              padding: 6px;
              font-size: 13px;
            }
            th {
              background-color: #f8f8f8;
            }
            .right {
              text-align: right;
            }
            tfoot td {
              font-weight: bold;
            }
            hr {
              border: none;
              border-top: 1px dashed #000;
              margin: 10px 0;
            }
            .note {
              font-size: 11px;
              font-style: italic;
              margin-top: 15px;
            }
            @media print {
              body {
                width: 100%;
                margin: 0;
                padding: 0;
                text-align: center;
              }
              table {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${logo ? `<img src="${logo}" class="logo" alt="Logo">` : ""}
          <h4>Annys Restaurant & Bar</h4>
          <h6>CUSTOMER BILL</h6>
          <hr>
          <div style="text-align:centered; font-size:13px;">
            <strong>Order No:</strong> ${orderId}<br>
            <strong>Waiter:</strong> ${waiterName}<br>
            <strong>Table:</strong> ${chosenTable || "N/A"}
          </div>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Price (£)</th>
                <th>Qty</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
            <tfoot>
              <tr>
                <td colspan="2">Total</td>
                <td class="right">${totalOrdersQty}</td>
                <td class="right">£ ${totalOrdersPrice}</td>
              </tr>
              <tr>
                <td colspan="3">Discount</td>
                <td class="right">£ 0.00</td>
              </tr>
              <tr>
                <td colspan="3">Grand Total</td>
                <td class="right">£ ${totalOrdersPrice}</td>
              </tr>
            </tfoot>
          </table>
          <div class="note">
            NB: THIS IS NOT AN OFFICIAL PAYMENT RECEIPT. PLEASE INSIST ON GETTING A RECEIPT AFTER PAYMENT.
          </div>
        </body>
      </html>
    `;
};


// Generate the HTML content for the receipt print window
const generatePrintReceiptHTML = (
  orderId,
  waiterName,
  chosenTable,
  totalOrdersQty,
  totalOrdersPrice,
  orderItems,
  totalPaid,
  cashValue,
  cardValue,
  change,
  logo
) => {
  const rows = orderItems
    .map((item) => {
      console.log("item to print from receipt", item);
      const name =
        item?.menuItems?.type === "food"
          ? `${item.menuItems.item_name.toUpperCase()} ${
              item.menuItems.description
                ? item.menuItems.description.toUpperCase()
                : ""
            }`
          : item?.drink?.type === "drink"
          ? item.drink.item_name.toUpperCase()
          : "UNKNOWN ITEM";

      const price =
        item?.menuItems?.price || item?.drink?.price || item.price || 0;

      return `
          <tr>
            <td>${name}</td>
            <td class="right">${price.toFixed(2)}</td>
            <td class="right">${item.quantity}</td>
            <td class="right">${item.total.toFixed(2)}</td>
          </tr>
        `;
    })
    .join("");

  return `
      <html>
        <head>
          <title>Print Receipt</title>
          <style>
            * {
              box-sizing: border-box;
            }
            body {
              font-family: "Arial", sans-serif;
              width: 80mm;
              margin: 0 auto;
              text-align: center;
              padding: 10px;
            }
            .logo {
              display: block;
              margin: 0 auto 10px auto;
              width: 80px;
              height: 80px;
              object-fit: contain;
            }
            h4 {
              margin: 5px 0;
              font-weight: bold;
            }
            h6 {
              margin: 0;
              font-style: italic;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
            }
            th, td {
              border: 1px solid #000;
              padding: 6px;
              font-size: 13px;
            }
            th {
              background-color: #f8f8f8;
            }
            .right {
              text-align: right;
            }
            tfoot td {
              font-weight: bold;
            }
            hr {
              border: none;
              border-top: 1px dashed #000;
              margin: 10px 0;
            }
            .note {
              font-size: 11px;
              font-style: italic;
              margin-top: 15px;
              text-align: center;
            }
            @media print {
              body {
                width: 100%;
                margin: 0;
                padding: 0;
                text-align: center;
              }
              table {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${logo ? `<img src="${logo}" class="logo" alt="Logo">` : ""}
          <h4>Annys Restaurant & Bar</h4>
          <h6>OFFICIAL RECEIPT</h6>
          <hr>
          <div style="font-size:13px;">
            <strong>Order No:</strong> ${orderId}<br>
            <strong>Waiter:</strong> ${waiterName}<br>
            <strong>Table:</strong> ${chosenTable || "N/A"}
          </div>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Price (£)</th>
                <th>Qty</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
            <tfoot>
              <tr>
                <td colspan="2">Total Items</td>
                <td class="right">${totalOrdersQty}</td>
                <td class="right">£ ${totalOrdersPrice}</td>
              </tr>
              <tr>
                <td colspan="3">Amount Paid</td>
                <td class="right">£ ${totalPaid}</td>
              </tr>
              <tr>
                <td colspan="3">Cash</td>
                <td class="right">£ ${cashValue}</td>
              </tr>
              <tr>
                <td colspan="3">Card</td>
                <td class="right">£ ${cardValue}</td>
              </tr>
              <tr>
                <td colspan="3">Change</td>
                <td class="right">£ ${change}</td>
              </tr>
            </tfoot>
          </table>
          <div class="note">
            THANK YOU FOR YOUR PATRONAGE!<br>
            <em>Please keep this receipt as proof of payment.</em>
          </div>
        </body>
      </html>
    `;
};

// Function to generate the HTML content for the kitchen print
const generateKitchenPrintHTML = (
  orderId,
  waiterName,
  chosenTable,
  orderItems,
  logo
) => {
  const rows = orderItems
    .filter((item) => item?.menuItems?.type === "food")
    .map((item) => {
      const name = `${item.menuItems.item_name.toUpperCase()}${
        item.menuItems.description
          ? " " + item.menuItems.description.toUpperCase()
          : ""
      }`;
      return `
          <tr>
            <td>${name}</td>
            <td class="right">${item.quantity}</td>
          </tr>
        `;
    })
    .join("");

  return `
      <html>
        <head>
          <title>Kitchen Print</title>
          <style>
            * {
              box-sizing: border-box;
            }
            body {
              font-family: "Arial", sans-serif;
              width: 80mm;
              margin: 0 auto;
              text-align: center;
              padding: 10px;
            }
            .logo {
              display: block;
              margin: 0 auto 10px auto;
              width: 70px;
              height: 70px;
              object-fit: contain;
            }
            h4 {
              margin: 0;
              font-weight: bold;
              font-size: 16px;
            }
            p {
              font-size: 13px;
              margin: 8px 0;
              text-align: center;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid #000;
              padding: 6px;
              font-size: 13px;
            }
            th {
              background: #f8f8f8;
            }
            .right {
              text-align: right;
            }
            hr {
              border: none;
              border-top: 1px dashed #000;
              margin: 10px 0;
            }
            @media print {
              body {
                width: 100%;
                margin: 0;
                padding: 0;
                text-align: center;
              }
              table {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${logo ? `<img src="${logo}" class="logo" alt="Logo"/>` : ""}
          <h4>Annys Restaurant & Bar</h4>
          <hr/>
          <p>
            <strong>ORDER NO:</strong> ${orderId}<br/>
            <strong>SERVED BY:</strong> ${waiterName}<br/>
            <strong>TABLE:</strong> ${chosenTable || "N/A"}
          </p>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `;
};
